import { GlobalStyles, Spinner } from '@cimpress/react-components';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import styles from './App.module.scss';
import AppHeader from './components/AppHeader';
import auth from './utilities/authentication';
import EntityManagementPage from './pages/EntityManagementPage';
// import AutoflowsPage from './pages/AutoflowsPage';
// import UserflowsPage from './pages/UserflowsPage';
// import IssuesPage from './pages/IssuesPage';
// import IntegrationPage from './pages/IntegrationPage';
import EntityTypePage from './pages/EntityTypePage';
import FulfillerLayout from './FulfillerLayout';
import { useFulfiller } from './FulfillerContext';
import CreateEntityTypePage from './pages/CreateEntityTypePage';
import EditEntityTypePage from './pages/EditEntityTypePage';
import NotFoundPage from './pages/NotFoundPage';
import { useCallback, useEffect, useState } from 'react';
import {
  getAccountId,
  getCanonicalId,
  getTZ,
  isInCometPortal,
  isUserLoggedIn,
  subscribeTZUpdates,
} from './utilities/helper';
import { fetchUserPermissions } from './services/access.management.service';

import { useDispatch } from 'react-redux';
import { setPermissions } from './store/permissionSlice';
import { retrieveFulfillerList } from './utilities/fulfillerHelper';
import { setTimeZone } from './store/timeZoneSlice';

import { updateCurrentPath } from '@cimpress-technology/web-shell-communication';

function Layout() {
  return (
    <>
      <div className={styles.rootDiv}>
        <AppHeader auth={auth} />
        <Outlet /> {/* Outlet renders child route elements here */}
      </div>
    </>
  );
}

export function TenantGate() {
  const { fulfillerData } = useFulfiller();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const currentTenant = params.get('tenantId');

  const tenantId = fulfillerData?.fulfillerLocation.value ?? currentTenant;

  if (!tenantId) {
    return <Outlet />;
  }

  if (tenantId && currentTenant && currentTenant !== tenantId) {
    return <Outlet />;
  }

  if (location.pathname === '/') {
    return (
      <Navigate
        to={`/entitytypes?tenantId=${tenantId ?? currentTenant}`}
        replace
      />
    );
  }

  if (location.pathname.startsWith('/entitytypes') && !currentTenant) {
    return (
      <Navigate
        to={`${location.pathname}?tenantId=${tenantId ?? currentTenant}`}
        replace
      />
    );
  }

  return <Outlet />;
}

const App = () => {
  const { fulfillerData, setFulfillerDetails } = useFulfiller();
  const dispatch = useDispatch();
  const APP_ENV: string = import.meta.env.VITE_APP_ENV;
  const location = useLocation();
  const [isPortalConfigLoading, setIsPortalConfigLoading] = useState(false);
  const [isPortalConfigTimedOut, setIsPortalConfigTimedOut] = useState(false);

  // Function to fetch and dispatch user permissions
  const fetchAndSetPermissions = useCallback(
    async (fulfillmentCometAccountId: string | undefined) => {
      let userPermissions = { read: false, create: false, update: false };
      if (APP_ENV === 'development') {
        userPermissions = { read: true, create: true, update: true };
      } else {
        const canonicalId = await getCanonicalId();
        userPermissions = await fetchUserPermissions(
          canonicalId || '',
          fulfillmentCometAccountId || '',
        );
      }
      dispatch(setPermissions(userPermissions));
    },
    [dispatch, APP_ENV],
  );

  useEffect(() => {
    const setPermissionsOnLoad = async () => {
      if (auth.isLoggedIn() && fulfillerData) {
        await fetchAndSetPermissions(fulfillerData.xCometAccountId);
      }
    };
    setPermissionsOnLoad();
  }, [fulfillerData, fetchAndSetPermissions]);

  useEffect(() => {
    const initializeApp = async () => {
      if (!isInCometPortal()) {
        return;
      }

      setIsPortalConfigLoading(true);
      setIsPortalConfigTimedOut(false);

      try {
        const isLoggedIn = await isUserLoggedIn();
        if (!isLoggedIn) return;

        const tenants = await retrieveFulfillerList();

        const fulfillmentCometAccountId = await getAccountId();
        if (!fulfillmentCometAccountId) return;

        const selectedTenant = tenants.find(
          (tenant) => tenant.xCometAccountId === fulfillmentCometAccountId,
        );

        if (selectedTenant) {
          const matchedConfig = {
            fulfiller: selectedTenant.fulfiller,
            fulfillerLocation: selectedTenant.fulfillerLocations[0],
            xCometAccountId: selectedTenant.xCometAccountId,
          };

          setFulfillerDetails({
            fulfillerData: matchedConfig,
            xCometAccountId: matchedConfig.xCometAccountId,
          });

          const timezone = await getTZ();
          dispatch(setTimeZone(timezone));
          await fetchAndSetPermissions(fulfillmentCometAccountId);
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
      } finally {
        setIsPortalConfigLoading(false);
      }
    };

    initializeApp();
  }, [dispatch, fetchAndSetPermissions, setFulfillerDetails]);

  useEffect(() => {
    if (!isInCometPortal() || !isPortalConfigLoading) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsPortalConfigTimedOut(true);
    }, 30000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isPortalConfigLoading]);

  useEffect(() => {
    if (!isInCometPortal()) return;
    let sub: { unsubscribe: () => void } | null = null;
    subscribeTZUpdates((tz) => dispatch(setTimeZone(tz))).then((s) => {
      sub = s;
    });
    // Unsubscribe on unmount to prevent stale dispatches and memory leaks from
    // the long-lived RxJS observable that the parent Comet Portal keeps open.
    return () => {
      sub?.unsubscribe();
    };
  }, [dispatch]);

  // Update current path for web shell communication
  useEffect(() => {
    updateCurrentPath(location.pathname + location.search);
  }, [location]);

  if (isInCometPortal() && isPortalConfigLoading) {
    return (
      <>
        <GlobalStyles />
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            textAlign: 'center',
            padding: '24px',
          }}
        >
          <Spinner size="large" />
          <p>
            {isPortalConfigTimedOut
              ? 'Still fetching Comet Portal configuration. Please wait or refresh the page.'
              : 'Loading your tenant configuration...'}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <GlobalStyles />
      <Routes>
        <Route element={<Layout />}>
          <Route element={<TenantGate />}>
            <Route path="/" element={<FulfillerLayout />}>
              <Route path="entitytypes" element={<EntityManagementPage />} />
              <Route
                path="entitytypes/create"
                element={<CreateEntityTypePage />}
              />
              <Route
                path="entitytypes/:entityTypeId"
                element={<EntityTypePage />}
              />
              <Route
                path="entitytypes/:entityTypeId/edit"
                element={<EditEntityTypePage />}
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Route>
        </Route>
      </Routes>
    </>
  );
};

export default App;
